import React, {createContext, FC, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react'
import {
    getChatNotifications,
    getUnreadGrouped,
    markDriverChatDelivered,
    updateChatNotifications,
} from '@containers/chat/api/api'
import {ChatIncoming, ChatRouteGroup, normalizePlate} from '@containers/chat/types'
import {playChatSound, setChatSoundEnabled} from '@containers/chat/helpers/notificationSound'
import ChatNotifications, {ChatPopup} from '@containers/chat/notifications/ChatNotifications'
import ChatModal from '@containers/chat/widget/ChatModal'
// Styles are imported here (not only in the modal): toasts appear without the modal being open.
import '@containers/chat/chat.css'

const POLL_MS = 10000                                   // same interval as the legacy GlobalChat
const STATES_KEY = 'chat:previousDriverStates'          // tells NEW messages from already seen ones

export interface OpenChatTarget {
    driverId: number
    driverName?: string | null
    plate?: string
    routeName?: string
}

interface ChatContextValue {
    unreadTotal: number
    groups: ChatRouteGroup[]
    /**
     * Unread count per driver. This is the freshest source there is: it comes from the chat poll,
     * which is what already drives the sound and the toasts. Screens that show an unread marker of
     * their own (the stadium card) read it from here instead of their own snapshot, which lags.
     */
    unreadByDriver: ReadonlyMap<number, number>
    /** Same counts keyed by plate, for vehicles whose voyage has no assigned driver. */
    unreadByPlate: ReadonlyMap<string, number>
    /** Last batch of genuinely new messages; null until one arrives. */
    incoming: ChatIncoming | null
    /** User setting: sound and toasts for new messages. The counter and markers ignore it. */
    notificationsEnabled: boolean
    setNotificationsEnabled: (value: boolean) => void
    openChat: (target: OpenChatTarget) => void      // open a driver thread from any screen
    openList: () => void                            // open the modal with the conversation list
    close: () => void
    refresh: () => void
}

const ChatContext = createContext<ChatContextValue | null>(null)

export const useChat = (): ChatContextValue => {
    const ctx = useContext(ChatContext)
    if (!ctx) {
        throw new Error('useChat must be used within ChatProvider')
    }
    return ctx
}

const loadStates = (): Record<string, number> => {
    try {
        return JSON.parse(localStorage.getItem(STATES_KEY) || '{}')
    } catch {
        return {}
    }
}

const saveStates = (states: Record<string, number>): void => {
    try {
        localStorage.setItem(STATES_KEY, JSON.stringify(states))
    } catch {
        // ignore storage errors
    }
}

/**
 * Global chat: polls account-wide unread counts (grouped by route), plays a sound and shows
 * popup notifications for NEW messages; owns the modal with the conversation list and thread.
 * Any screen can open a thread through useChat().openChat(...).
 */
export const ChatProvider: FC<{children: ReactNode}> = ({children}) => {
    const [groups, setGroups] = useState<ChatRouteGroup[]>([])
    const [popups, setPopups] = useState<ChatPopup[]>([])
    const [modalOpen, setModalOpen] = useState(false)
    const [target, setTarget] = useState<OpenChatTarget | null>(null)
    const [incoming, setIncoming] = useState<ChatIncoming | null>(null)
    const [notificationsEnabled, setNotificationsState] = useState(true)
    // refresh() runs from an interval, so it reads the setting through a ref, not a stale closure.
    const notificationsRef = useRef(true)
    // The first poll already reports messages as new, so polling waits for the setting.
    const [settingLoaded, setSettingLoaded] = useState(false)
    const prevStatesRef = useRef<Record<string, number>>(loadStates())

    const applyNotifications = useCallback((value: boolean) => {
        notificationsRef.current = value
        setNotificationsState(value)
        setChatSoundEnabled(value)
        if (!value) {
            setPopups([])
        }
    }, [])

    useEffect(() => {
        getChatNotifications()
            .then((res) => applyNotifications(res?.enabled !== false))
            .catch(() => undefined)
            .finally(() => setSettingLoaded(true))
    }, [applyNotifications])

    const setNotificationsEnabled = useCallback((value: boolean) => {
        const previous = notificationsRef.current
        applyNotifications(value)
        updateChatNotifications(value).catch(() => applyNotifications(previous))
    }, [applyNotifications])

    const unreadTotal = useMemo(
        () => groups.reduce((sum, g) => sum + g.drivers.reduce((s, d) => s + (d.unreadCount ?? 0), 0), 0),
        [groups],
    )

    // Only drivers with something unread are listed, so an absent key means "nothing unread" —
    // that is what makes a marker disappear once the thread is read. Both maps are built at once:
    // consumers match on whichever identifier they happen to have.
    const [unreadByDriver, unreadByPlate] = useMemo(() => {
        const byDriver = new Map<number, number>()
        const byPlate = new Map<string, number>()
        groups.forEach((group) => {
            (group.drivers ?? []).forEach((driver) => {
                const count = driver.unreadCount ?? 0
                if (count <= 0) {
                    return
                }
                byDriver.set(driver.driverId, count)
                const plate = normalizePlate(driver.licensePlate)
                if (plate) {
                    byPlate.set(plate, count)
                }
            })
        })
        return [byDriver, byPlate] as const
    }, [groups])

    const dismissPopup = useCallback((driverId: number) => {
        setPopups((prev) => prev.filter((p) => p.driverId !== driverId))
    }, [])

    const openChat = useCallback((next: OpenChatTarget) => {
        setTarget(next)
        setModalOpen(true)
        dismissPopup(next.driverId)
    }, [dismissPopup])

    const openList = useCallback(() => {
        setTarget(null)
        setModalOpen(true)
    }, [])

    const close = useCallback(() => {
        setModalOpen(false)
        setTarget(null)
    }, [])

    const refresh = useCallback(async () => {
        const data = await getUnreadGrouped().catch(() => null)
        if (!data) {
            return
        }
        setGroups(data)

        // Compare with the previous snapshot so popups are shown only for NEW messages.
        const prev = prevStatesRef.current
        const next: Record<string, number> = {}
        const fresh: ChatPopup[] = []
        data.forEach((group: ChatRouteGroup) => {
            (group.drivers ?? []).forEach((driver) => {
                const count = driver.unreadCount ?? 0
                next[driver.driverId] = count
                if (count > (prev[driver.driverId] ?? 0)) {
                    fresh.push({
                        driverId: driver.driverId,
                        driverName: driver.driverName,
                        plate: driver.licensePlate,
                        routeName: group.routeName,
                        routeId: group.routeId,
                        unreadCount: count,
                        messageType: driver.lastMessage?.messageType ?? driver.messageType,
                    })
                }
            })
        })
        prevStatesRef.current = next
        saveStates(next)

        if (fresh.length) {
            // Same detection that drives the sound, published for screens that want to react to it.
            setIncoming({
                drivers: fresh.map((p) => ({driverId: p.driverId, plate: p.plate, routeId: p.routeId})),
                at: Date.now(),
            })
            if (notificationsRef.current) {
                playChatSound()
                setPopups((prevPopups) => {
                    const byId = new Map(prevPopups.map((p) => [p.driverId, p]))
                    fresh.forEach((p) => byId.set(p.driverId, p))
                    return Array.from(byId.values())
                })
            }
            // Mark as delivered, the same way the legacy widget did when showing a popup. Done with
            // notifications off too: the message still reached the dispatcher through the counter.
            fresh.forEach((p) => markDriverChatDelivered(p.driverId).catch(() => undefined))
        }
    }, [])

    useEffect(() => {
        if (!settingLoaded) {
            return
        }
        refresh()
        const timer = window.setInterval(refresh, POLL_MS)
        return () => window.clearInterval(timer)
    }, [refresh, settingLoaded])

    const value = useMemo<ChatContextValue>(
        () => ({
            unreadTotal, groups, unreadByDriver, unreadByPlate, incoming,
            notificationsEnabled, setNotificationsEnabled, openChat, openList, close, refresh,
        }),
        [unreadTotal, groups, unreadByDriver, unreadByPlate, incoming,
            notificationsEnabled, setNotificationsEnabled, openChat, openList, close, refresh],
    )

    return (
        <ChatContext.Provider value={value}>
            {children}
            <ChatNotifications popups={popups} onOpen={openChat} onDismiss={dismissPopup}/>
            {modalOpen && <ChatModal initialTarget={target} onClose={close}/>}
        </ChatContext.Provider>
    )
}